import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Container, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, Button, IconButton, Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  useTheme, useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { UserRoleContext } from './Layout';
import { productsApi, lambdaGroupsApi } from '../services/api';
import ProductForm from './ProductForm';

interface Product {
  _id: string;
  name: string;
  width: number;
  thickness: number;
  length: number;
  lambdaGroup?: string;
}



const ProductAdmin: React.FC = () => {
  const { role } = useContext(UserRoleContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Forma za novi/izmenu
  const [form, setForm] = useState<{ name: string; width: string; thickness: string; length: string; lambdaGroup: string }>({
    name: '', width: '', thickness: '', length: '', lambdaGroup: ''
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Dodaj state za šablone proizvoda
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [currentTemplateProductId, setCurrentTemplateProductId] = useState<string | null>(null);

  // Dodaj state za potvrdu brisanja
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null);

  // Dodaj state za grupe proizvoda
  const [lambdaGroups, setLambdaGroups] = useState<string[]>([]);
  const [lambdaGroupDialogOpen, setLambdaGroupDialogOpen] = useState(false);
  const [newLambdaGroup, setNewLambdaGroup] = useState('');
  const [editLambdaGroupIdx, setEditLambdaGroupIdx] = useState<number | null>(null);
  const [editLambdaGroupValue, setEditLambdaGroupValue] = useState('');

  // Ref for ProductForm to trigger handleSaveTemplate
  const productFormRef = useRef<{ handleSaveTemplate: () => void }>(null);

  // State za paginaciju i search
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(12);

  const fetchLambdaGroups = () => {
    lambdaGroupsApi.getAll()
      .then(res => setLambdaGroups(res.map((g: any) => g.name)))
      .catch(() => setLambdaGroups([]));
  };

  useEffect(() => {
    fetchLambdaGroups();
  }, []);

  useEffect(() => { fetchProducts(); }, []);

  // Funkcije za filtriranje i paginaciju
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.lambdaGroup?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.width.toString().includes(searchTerm) ||
    product.thickness.toString().includes(searchTerm) ||
    product.length.toString().includes(searchTerm)
  );

  const paginatedProducts = filteredProducts.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredProducts.length / rowsPerPage);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0); // Reset na prvu stranicu kada se menja search
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await productsApi.getAll();
      setProducts(res);
    } catch (e) {
      setError('Greška pri dohvatanju proizvoda');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleDialogOpen = (product?: Product) => {
    if (product) {
      setEditId(product._id);
      setForm({
        name: product.name,
        width: product.width.toString(),
        thickness: product.thickness.toString(),
        length: product.length.toString(),
        lambdaGroup: product.lambdaGroup || '', // Assuming lambdaGroup is part of the product object
      });
    } else {
      setEditId(null);
      setForm({ name: '', width: '', thickness: '', length: '', lambdaGroup: '' });
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm({ name: '', width: '', thickness: '', length: '', lambdaGroup: '' });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        name: form.name.trim(),
        width: Number(form.width),
        thickness: Number(form.thickness),
        length: Number(form.length),
        lambdaGroup: form.lambdaGroup,
      };
      if (!payload.name || !payload.width || !payload.thickness || !payload.length) {
        setError('Sva polja su obavezna');
        setLoading(false);
        return;
      }
      if (editId) {
        await productsApi.update(editId, payload);
        setSuccess('Proizvod izmenjen');
      } else {
        await productsApi.create(payload);
        setSuccess('Proizvod dodat');
      }
      fetchProducts();
      handleDialogClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri čuvanju');
    } finally {
      setLoading(false);
    }
  };

  // Izmeni handleDelete da samo otvara dijalog
  const handleDeleteClick = (id: string) => {
    setDeleteProductId(id);
    setDeleteDialogOpen(true);
  };

  // Nova funkcija koja zaista briše
  const handleDeleteConfirm = async () => {
    if (!deleteProductId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await productsApi.delete(deleteProductId);
      setSuccess('Proizvod obrisan');
      fetchProducts();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri brisanju');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteProductId(null);
    }
  };

  const handleTemplateOpen = (product: Product) => {
    setCurrentTemplateProductId(product._id);
    setShowTemplateForm(true);
  };
  const handleTemplateFormClose = () => {
    setShowTemplateForm(false);
    setCurrentTemplateProductId(null);
  };
  const handleTemplateFormSave = () => {
    setShowTemplateForm(false);
    setCurrentTemplateProductId(null);
    // Osveži šablone ako treba
  };

  if (role !== 'admin' && role !== 'master_admin') {
    return <Container maxWidth="md"><Alert severity="error">Samo admin ili master admin može da pristupi ovoj stranici.</Alert></Container>;
  }

  return (
    <Container maxWidth="md" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 3 }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
        Administracija proizvoda
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 2,
          gap: isMobile ? 1 : 0
        }}>
          <Typography variant={isMobile ? "h6" : "h6"} sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
            Proizvodi
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen()}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            Dodaj proizvod
          </Button>
        </Box>

        {/* Search polje */}
        <TextField
          label="Pretraži proizvode..."
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Pretraži po nazivu, lambda grupi, širini, debljini, dužini..."
          sx={{ mb: 2 }}
        />

        <TableContainer sx={{ maxHeight: isMobile ? 400 : 600 }}>
          <Table stickyHeader size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                  Naziv
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Širina (mm)
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Debljina (mm)
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Dužina (mm)
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Lambda grupa
                    </TableCell>
                  </>
                )}
                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                  Akcije
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.length > 0 ? (
                paginatedProducts.map(p => (
                  <TableRow key={p._id}>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {p.name}
                        </Typography>
                        {isMobile && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {p.width}×{p.thickness}×{p.length}mm | {p.lambdaGroup || 'N/A'}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{p.width}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{p.thickness}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{p.length}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{p.lambdaGroup || '-'}</TableCell>
                      </>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleDialogOpen(p)} title="Uredi proizvod">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(p._id)} title="Obriši proizvod">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleTemplateOpen(p)} title="Uredi šablon">
                          📝
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isMobile ? 2 : 6} align="center">
                    {searchTerm ? 'Nema proizvoda koji odgovaraju pretrazi' : 'Nema proizvoda'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Informacija o broju proizvoda */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {searchTerm 
              ? `Prikazano ${paginatedProducts.length} od ${filteredProducts.length} proizvoda koji odgovaraju pretrazi`
              : `Ukupno ${products.length} proizvoda`
            }
          </Typography>
        </Box>

                  {/* Paginacija */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center', 
              alignItems: 'center', 
              mt: 2, 
              gap: isMobile ? 1 : 1 
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 0.5,
                flexWrap: 'wrap'
              }}>
                <Button 
                  onClick={() => handlePageChange(0)} 
                  disabled={currentPage === 0}
                  size={isMobile ? "small" : "small"}
                  variant="outlined"
                >
                  Prva
                </Button>
                <Button 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 0}
                  size={isMobile ? "small" : "small"}
                  variant="outlined"
                >
                  Prethodna
                </Button>
                <Button 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage >= totalPages - 1}
                  size={isMobile ? "small" : "small"}
                  variant="outlined"
                >
                  Sledeća
                </Button>
                <Button 
                  onClick={() => handlePageChange(totalPages - 1)} 
                  disabled={currentPage >= totalPages - 1}
                  size={isMobile ? "small" : "small"}
                  variant="outlined"
                >
                  Poslednja
                </Button>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  mx: isMobile ? 0 : 2,
                  mt: isMobile ? 1 : 0,
                  textAlign: 'center',
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}
              >
                Stranica {currentPage + 1} od {totalPages} ({filteredProducts.length} proizvoda)
              </Typography>
            </Box>
          )}
      </Paper>
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
          {editId ? 'Izmeni proizvod' : 'Dodaj proizvod'}
        </DialogTitle>
        <DialogContent sx={{ pt: isMobile ? 2 : 1 }}>
          <TextField
            label="Naziv proizvoda"
            name="name"
            value={form.name}
            onChange={handleInput}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="λ Grupa proizvoda"
            name="lambdaGroup"
            value={form.lambdaGroup}
            onChange={handleInput}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ mb: 2 }}
          >
            {lambdaGroups.map(group => (
              <MenuItem key={group} value={group}>{group}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Širina (mm)"
            name="width"
            value={form.width}
            onChange={handleInput}
            type="number"
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Debljina (mm)"
            name="thickness"
            value={form.thickness}
            onChange={handleInput}
            type="number"
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Dužina (mm)"
            name="length"
            value={form.length}
            onChange={handleInput}
            type="number"
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button 
            onClick={handleDialogClose}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
          >
            Otkaži
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
          >
            {editId ? 'Sačuvaj izmene' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>
      {showTemplateForm && currentTemplateProductId && (
        <Dialog 
          open={showTemplateForm} 
          onClose={handleTemplateFormClose} 
          maxWidth="lg" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
            Uredi šablon forme za proizvod
          </DialogTitle>
          <DialogContent sx={{ pt: isMobile ? 2 : 1 }}>
            <ProductForm ref={productFormRef} templateMode templateProductId={currentTemplateProductId} onTemplateSave={handleTemplateFormSave} />
          </DialogContent>
          <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
            <Button 
              onClick={handleTemplateFormClose}
              size={isMobile ? "large" : "medium"}
              fullWidth={isMobile}
            >
              Otkaži
            </Button>
            <Button 
              onClick={() => productFormRef.current?.handleSaveTemplate()} 
              variant="contained"
              size={isMobile ? "large" : "medium"}
              fullWidth={isMobile}
            >
              Sačuvaj šablon
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
          Obrisati proizvod?
        </DialogTitle>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
          >
            Otkaži
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
          >
            Obriši
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductAdmin; 